'use client';

import {Modal, type ModalProps} from '@udemy-v2/react-dialog-components';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {ExpressiveIcon, type ExpressiveIconName} from '@udemy-v2/icons-expressive';
import styles from './personal-plan-includes-modal.module.scss';

export interface PersonalPlanIncludesModalProps {
    isOpen: ModalProps['isOpen'];
    onClose: NonNullable<ModalProps['onClose']>;
    courseCount: number;
}

export const PersonalPlanIncludesModal = ({
    isOpen,
    onClose,
    courseCount,
}: PersonalPlanIncludesModalProps) => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();

    const items: {name: ExpressiveIconName; text: string}[] = [
        {
            name: 'content',
            text: interpolate(
                gettext(
                    '%(courses_count)s+ top-rated courses including AI, web development, data science, and IT',
                ),
                {courses_count: formatNumber(courseCount)},
                true,
            ),
        },
        {
            name: 'soft-skills',
            text: gettext(
                'AI-powered soft skills practice through interactive role play scenarios',
            ),
        },
        {
            name: 'learning-labs',
            text: gettext('Hands-on technical practice through labs and coding exercises'),
        },
        {
            name: 'certificate',
            text: gettext(
                'Comprehensive certification prep for AWS, Microsoft, CompTIA, PMI, and more',
            ),
        },
        {
            name: 'marketing',
            text: gettext('Ad-free'),
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={gettext("What's included in Personal Plan")}
            data-purpose="personal-plan-includes-modal"
        >
            <ul className={styles['list']} data-purpose="personal-plan-includes-modal-list">
                {items.map((item) => (
                    <li key={item.name} className={styles['item']}>
                        <ExpressiveIcon
                            className={styles['icon']}
                            name={item.name}
                            colorScheme="light"
                            size="medium"
                        />
                        <span className={styles['text']}>{item.text}</span>
                    </li>
                ))}
            </ul>
            <div className={styles['footer']}>
                <Button
                    udStyle="primary"
                    onClick={onClose}
                    data-purpose="personal-plan-includes-modal-close"
                >
                    {gettext('Close')}
                </Button>
            </div>
        </Modal>
    );
};
